"use client";

import Image from "next/image";
import { useState } from "react";
import { Loader2, LockKeyhole } from "lucide-react";

export default function LoginPage() {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await fetch("/api/auth/login", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ username, password }),
            });
            if (!response.ok) {
                setError(
                    response.status === 429
                        ? "로그인 시도가 너무 많습니다. 잠시 후 다시 시도해 주세요."
                        : "아이디 또는 비밀번호를 확인해 주세요.",
                );
                return;
            }

            const next = new URLSearchParams(window.location.search).get("next");
            window.location.assign(next?.startsWith("/") && !next.startsWith("//") ? next : "/");
        } catch {
            setError("로그인 서버에 연결할 수 없습니다.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10">
            <div className="w-full max-w-md rounded-[2rem] border border-slate-100 bg-white p-7 shadow-xl shadow-slate-200/60 sm:p-9">
                <div className="flex items-center gap-3">
                    <div className="flex h-16 w-16 items-center justify-center rounded-[1.25rem] bg-emerald-600 p-2 shadow-lg shadow-emerald-200">
                        <Image
                            src="/brand/ciat-symbol.png"
                            alt="C:AT 씨앗"
                            width={64}
                            height={64}
                            className="h-full w-full scale-110 object-contain brightness-0 invert"
                            priority
                        />
                    </div>
                    <div>
                        <div className="text-xl font-black tracking-tight text-slate-900">HYDRO-CLOUD</div>
                        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">
                            Admin Console
                        </div>
                    </div>
                </div>

                <div className="mt-8">
                    <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-blue-500">
                        <LockKeyhole className="h-4 w-4" /> Secure Access
                    </div>
                    <h1 className="mt-3 text-2xl font-black text-slate-900">관리자 로그인</h1>
                    <p className="mt-2 text-sm font-medium text-slate-500">
                        관리 시스템에 접근하려면 관리자 계정으로 로그인하세요.
                    </p>
                </div>

                <form className="mt-7 space-y-4" onSubmit={submit}>
                    <label className="block text-sm font-bold text-slate-700">
                        아이디
                        <input
                            value={username}
                            onChange={(event) => setUsername(event.target.value)}
                            autoComplete="username"
                            required
                            autoFocus
                            className="mt-2 h-12 w-full rounded-2xl border border-slate-200 px-4 font-medium outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                        />
                    </label>
                    <label className="block text-sm font-bold text-slate-700">
                        비밀번호
                        <input
                            type="password"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            autoComplete="current-password"
                            required
                            className="mt-2 h-12 w-full rounded-2xl border border-slate-200 px-4 font-medium outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                        />
                    </label>

                    {error && (
                        <div
                            role="alert"
                            className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-600"
                        >
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 font-black text-white shadow-lg shadow-blue-100 transition hover:bg-blue-700 disabled:opacity-60"
                    >
                        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                        로그인
                    </button>
                </form>
            </div>
        </div>
    );
}
